import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { makeAuthenticatedRequest } from '../services/api'; // Use the API service
import { FaCog, FaLink, FaFileInvoice, FaCloudDownloadAlt } from 'react-icons/fa';
import { initiateXeroConnection, handleXeroRedirectStatus } from '../utils/xeroUtils';
import AutomatedAlertsSettings from '../components/AutomatedAlertsSettings';

const SettingsPage = () => {
  const location = useLocation();
  const [xeroStatus, setXeroStatus] = useState(null);
  const [xeroMessage, setXeroMessage] = useState('');
  const [isLoadingConnect, setIsLoadingConnect] = useState(false);
  const [isLoadingTest, setIsLoadingTest] = useState(false);
  const [testInvoiceResult, setTestInvoiceResult] = useState(null);

  // Check for redirect status on component mount
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const redirectStatus = handleXeroRedirectStatus(queryParams);

    if (redirectStatus) {
      setXeroStatus(redirectStatus.success ? 'success' : 'error');
      setXeroMessage(redirectStatus.message);
      // Clear query params from URL after reading them
      window.history.replaceState({}, document.title, location.pathname);
    }
  }, [location]);

  const handleConnectXero = async () => {
    setIsLoadingConnect(true);
    setXeroStatus(null); // Reset status
    setXeroMessage('');
    setTestInvoiceResult(null); // Reset test result
    try {
      const authUrl = await initiateXeroConnection();
      // Redirect the browser to the URL provided by the backend
      window.location.href = authUrl;
    } catch (error) {
      console.error('Error initiating Xero connection:', error);
      setXeroStatus('error');
      setXeroMessage(error.message);
      setIsLoadingConnect(false); // Stop loading only on error
    }
  };

  const handleCreateTestInvoice = async () => {
    setIsLoadingTest(true);
    setTestInvoiceResult(null);
    setXeroStatus(null); // Clear previous Xero connection status messages
    setXeroMessage('');
    try {
      const response = await makeAuthenticatedRequest('/xero/invoices/test', {
        method: 'POST',
      });

      setTestInvoiceResult({ status: 'success', data: response });
    } catch (error) {
      console.error('Error creating test Xero invoice:', error);
      const message = error.response?.data?.message || error.message || 'An unknown error occurred.';
      setTestInvoiceResult({ status: 'error', message: message });
    } finally {
      setIsLoadingTest(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-jims-cyan-dark flex items-center">
          <FaCog className="mr-3" /> Settings
        </h1>
      </div>

      <div className="bg-white shadow-md rounded-lg p-6 border border-gray-200 space-y-6">

        {/* Xero Integration Section */}
        <div className="border-b pb-6">
          <h2 className="text-xl font-semibold text-gray-700 mb-4">Xero Integration</h2>

          {/* Display feedback from redirect */}
          {xeroStatus === 'success' && (
            <div className="mb-4 p-3 bg-green-100 text-green-700 border border-green-300 rounded">
              Xero connected successfully! {xeroMessage && `(${xeroMessage})`}
            </div>
          )}
          {xeroStatus === 'error' && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 border border-red-300 rounded">
              Xero connection failed: {xeroMessage}
            </div>
          )}

          <button
            onClick={handleConnectXero}
            disabled={isLoadingConnect}
            className="btn btn-primary inline-flex items-center"
          >
            <FaLink className="mr-2" />
            {isLoadingConnect ? 'Connecting...' : 'Connect to Xero'}
          </button>
          <p className="text-xs text-gray-500 mt-2">
            Connect your Xero account to enable invoice synchronization. You will be redirected to Xero to authorize the connection.
          </p>

          {/* New Button to Import Contacts */}
          <Link
            to="/settings/xero-import-contacts"
            className="btn btn-secondary inline-flex items-center mt-4" // Added mt-4 for spacing
          >
            <FaCloudDownloadAlt className="mr-2" />
            Xero Contacts
          </Link>
          <p className="text-xs text-gray-500 mt-2">
            Fetch contacts from Xero and stage them in the system.
          </p>
        </div>

        {/* Automated Alerts Section */}
        <AutomatedAlertsSettings />

        {/* Test Invoice Section */}
        <div>
          <h2 className="text-xl font-semibold text-gray-700 mb-4">Test Functions</h2>
          <button
            onClick={handleCreateTestInvoice}
            disabled={isLoadingTest || isLoadingConnect} // Disable if any loading is active
            className="btn btn-secondary inline-flex items-center"
          >
            <FaFileInvoice className="mr-2" />
            {isLoadingTest ? 'Creating...' : 'Create Test Xero Invoice'}
          </button>
          <p className="text-xs text-gray-500 mt-2">
            Create a sample invoice in your connected Xero account for testing purposes. Requires Xero connection.
          </p>

          {/* Display test invoice result */}
          {testInvoiceResult?.status === 'success' && (
            <div className="mt-4 p-3 bg-green-100 text-green-700 border border-green-300 rounded">
              <p className="font-semibold">Test invoice created successfully!</p>
              {testInvoiceResult.data?.invoiceId && <p>Invoice ID: {testInvoiceResult.data.invoiceId}</p>}
              {testInvoiceResult.data?.invoiceNumber && <p>Invoice Number: {testInvoiceResult.data.invoiceNumber}</p>}
            </div>
          )}
          {testInvoiceResult?.status === 'error' && (
            <div className="mt-4 p-3 bg-red-100 text-red-700 border border-red-300 rounded">
              <p className="font-semibold">Failed to create test invoice:</p>
              <p>{testInvoiceResult.message}</p>
            </div>
          )}
        </div>
      </div>
      <style jsx>{`
        .btn {
          padding: 0.6rem 1.2rem;
          font-weight: 600;
          border-radius: 0.375rem;
          transition: background-color 0.3s ease, opacity 0.3s ease;
          border: 1px solid transparent;
          cursor: pointer;
          font-size: 0.95rem;
        }
        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .btn-primary {
          background-color: #06B6D4;
          color: white;
        }
        .btn-primary:hover:not(:disabled) {
          background-color: #0E7490;
        }
        .btn-secondary {
          background-color: #E5E7EB;
          color: #1F2937;
          border: 1px solid #D1D5DB;
        }
        .btn-secondary:hover:not(:disabled) {
          background-color: #D1D5DB;
        }
      `}</style>
    </div>
  );
};

export default SettingsPage;
